import { computed, type Ref } from 'vue';
import type { TableElement } from '../model/form.types';
import { TABLE_CONSTRAINTS } from '../lib/table-constants';

export interface TableRowBreak {
  beforeRow: number;
  gapPx: number;
  pageNumber: number;
}

export interface TablePageGeometry {
  firstRowTop: number;
  pageHeight: number;
  contentStartOffset: number;
  bottomMargin: number;
}

interface ComputeTableRowBreaksParams {
  rows: number;
  rowHeights: number[];
  breakAllowedBefore: (rowIndex: number) => boolean;
  geometry: TablePageGeometry;
}

const MAX_BREAK_ITERATIONS = 5000;

function buildPrefixTops(rows: number, rowHeights: number[], firstRowTop: number): number[] {
  const prefixTop = new Array<number>(rows);
  let running = firstRowTop;
  for (let i = 0; i < rows; i++) {
    prefixTop[i] = running;
    running += rowHeights[i];
  }
  return prefixTop;
}

// Walk up from the overflowing row to the nearest row a break is allowed before, so a
// merged block is pushed down whole rather than bisected.
function resolveBreakRow(
  startRow: number,
  breakAllowedBefore: (rowIndex: number) => boolean
): number {
  let breakRow = startRow;
  while (breakRow > 0 && !breakAllowedBefore(breakRow)) {
    breakRow--;
  }
  return breakRow;
}

function rowBottomExceedsPage(
  rowTop: number,
  rowHeight: number,
  pageHeight: number,
  bottomMargin: number
): boolean {
  const page = Math.floor(rowTop / pageHeight);
  const usableBottom = (page + 1) * pageHeight - bottomMargin;
  return rowTop + rowHeight > usableBottom;
}

function makeBreak(
  breakRow: number,
  breakTop: number,
  pageHeight: number,
  contentStartOffset: number
): TableRowBreak | null {
  const breakPage = Math.floor(breakTop / pageHeight);
  const gapPx = (breakPage + 1) * pageHeight + contentStartOffset - breakTop;
  if (gapPx <= 0) return null;
  return { beforeRow: breakRow, gapPx, pageNumber: breakPage + 2 };
}

export function computeTableRowBreaks({
  rows,
  rowHeights,
  breakAllowedBefore,
  geometry,
}: ComputeTableRowBreaksParams): TableRowBreak[] {
  const { firstRowTop, pageHeight, contentStartOffset, bottomMargin } = geometry;

  if (rows <= 1 || pageHeight <= 0 || rowHeights.length !== rows) {
    return [];
  }

  const prefixTop = buildPrefixTops(rows, rowHeights, firstRowTop);
  const breaks: TableRowBreak[] = [];
  const brokenRows = new Set<number>();
  let gapSoFar = 0;
  let i = 0;
  let guard = 0;

  while (i < rows && guard++ < MAX_BREAK_ITERATIONS) {
    const crosses =
      i > 0 &&
      rowBottomExceedsPage(prefixTop[i] + gapSoFar, rowHeights[i], pageHeight, bottomMargin);
    const breakRow = crosses ? resolveBreakRow(i, breakAllowedBefore) : 0;
    const brk =
      breakRow > 0 && !brokenRows.has(breakRow)
        ? makeBreak(breakRow, prefixTop[breakRow] + gapSoFar, pageHeight, contentStartOffset)
        : null;

    if (brk) {
      breaks.push(brk);
      brokenRows.add(brk.beforeRow);
      gapSoFar += brk.gapPx;
      i = brk.beforeRow + 1;
      continue;
    }

    i++;
  }

  return breaks;
}

/**
 * A break is only allowed before a row when that row does not carry the
 * continuation of a merged cell rooted on an earlier row. This guarantees a
 * merged block is pushed to the next page as a whole and never bisected.
 */
export function isRowBreakAllowed(tableElement: TableElement, rowIndex: number): boolean {
  const rowCells = tableElement.properties.cells[rowIndex] ?? [];

  return !rowCells.some((cell) => {
    if (!cell.mergeRoot || cell.mergeRoot.row >= rowIndex) {
      return false;
    }

    return cell.isMerged === true || cell.merged === true;
  });
}

export function resolveTableRowHeights(tableElement: TableElement): number[] {
  const { rows, rowHeights } = tableElement.properties;
  if (rowHeights && rowHeights.length === rows) {
    return rowHeights.map((h) => Math.max(h, TABLE_CONSTRAINTS.MIN_ROW_HEIGHT));
  }
  return new Array<number>(rows).fill(TABLE_CONSTRAINTS.DEFAULT_ROW_HEIGHT);
}

export function useTablePageBreaks(
  tableElement: Ref<TableElement>,
  getGeometry: () => TablePageGeometry | null
) {
  const breaks = computed<TableRowBreak[]>(() => {
    const geometry = getGeometry();
    if (!geometry) return [];

    const rows = tableElement.value.properties.rows;
    const rowHeights = resolveTableRowHeights(tableElement.value);

    return computeTableRowBreaks({
      rows,
      rowHeights,
      breakAllowedBefore: (rowIndex) => isRowBreakAllowed(tableElement.value, rowIndex),
      geometry,
    });
  });

  const hasBreaks = computed(() => breaks.value.length > 0);

  const gapRowsBefore = (rowIndex: number): number => {
    let count = 0;
    for (const brk of breaks.value) {
      if (brk.beforeRow <= rowIndex) count++;
    }
    return count;
  };

  const gapBeforeRowMap = computed(() => {
    const map = new Map<number, number>();
    for (const brk of breaks.value) {
      map.set(brk.beforeRow, brk.gapPx);
    }
    return map;
  });

  return {
    breaks,
    hasBreaks,
    gapRowsBefore,
    gapBeforeRowMap,
  };
}
